/**
 * Phase 3 — Interactive WBS / Gantt view (Iter 200).
 * Merged into the Project Detail "Gantt" tab (DPR Live Test Findings Item 9) —
 * previously a standalone sidebar page, now embedded so there's one Gantt tab
 * per project instead of two competing views.
 *
 * Renders a plan's blocks + micro-tasks as a hand-rolled HTML/CSS timeline grid
 * (no heavy Gantt dependency). Bars are coloured by schedule status; hovering /
 * clicking a bar reveals baseline-vs-current dates and the schedule variance.
 * Editing is done through the existing Planner (link out) — drag-to-reschedule
 * is intentionally out of scope for this phase.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, RefreshCw, ChevronRight, ChevronDown, ExternalLink, Flag, GitBranch } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { formatDate as fmt, formatMonth } from '../../lib/format';

const DAY = 86400000;
const d = (s) => (s ? new Date(String(s).slice(0, 10) + 'T00:00:00') : null);

const STATUS_COLOR = {
  complete: { bar: 'bg-slate-400', text: 'text-slate-600', label: 'Complete' },
  delayed: { bar: 'bg-red-500', text: 'text-red-600', label: 'Delayed' },
  at_risk: { bar: 'bg-amber-500', text: 'text-amber-600', label: 'At risk' },
  on_track: { bar: 'bg-emerald-500', text: 'text-emerald-600', label: 'On track' },
  unknown: { bar: 'bg-slate-300', text: 'text-slate-500', label: '—' },
};

function computeStatus(item, bvaRow) {
  const today = new Date();
  if (item.actual_end || (bvaRow && bvaRow.actual_end)) return 'complete';
  if (bvaRow && bvaRow.status === 'delayed') return 'delayed';
  const end = d(item.end_date);
  if (end && end < today) return 'delayed';
  if (end) {
    const slack = Math.round((end - today) / DAY);
    if (slack >= 0 && slack < 5) return 'at_risk';
  }
  if (bvaRow && (bvaRow.status === 'ahead' || bvaRow.status === 'on_track')) return bvaRow.status === 'ahead' ? 'on_track' : 'on_track';
  return 'on_track';
}

// Embeddable Gantt panel — baseline vs current + critical path, scoped to one project.
// Used inside ProjectDetail's "Gantt" tab (no project selector — projectId comes from the page).
export function ProjectGanttPanel({ projectId }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [plan, setPlan] = useState(null);
  const [bva, setBva] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [showCritical, setShowCritical] = useState(false);
  const [criticalIds, setCriticalIds] = useState(new Set());
  const [cpmLoading, setCpmLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setPlan(null); setBva(null); setPlanId('');
    apiGet(`/project-planning/plans?project_id=${projectId}`).then((rows) => {
      setPlans(rows || []);
      const withBlocks = (rows || []).find(p => (p.block_count || 0) > 0) || (rows || [])[0];
      if (withBlocks) setPlanId(withBlocks.id);
    }).catch(() => setPlans([]));
  }, [projectId]);

  const loadPlan = (pid) => {
    if (!pid) return;
    setLoading(true);
    apiGet(`/project-planning/plans/${pid}`).then((p) => {
      setPlan(p);
      if (p?.baseline_frozen_at) {
        apiGet(`/project-planning/plans/${pid}/baseline-vs-actual`)
          .then(b => setBva(b)).catch(() => setBva(null));
      } else { setBva(null); }
    }).catch(() => toast.error('Failed to load plan')).finally(() => setLoading(false));
  };
  useEffect(() => { loadPlan(planId); /* eslint-disable-next-line */ }, [planId]);

  const bvaById = useMemo(() => {
    const m = {};
    (bva?.items || []).forEach(i => { m[i.id] = i; });
    return m;
  }, [bva]);

  const blocks = plan?.blocks || [];
  const microByBlock = useMemo(() => {
    const m = {};
    (plan?.micro_tasks || []).forEach(t => { (m[t.block_code] = m[t.block_code] || []).push(t); });
    return m;
  }, [plan]);

  const { minMs, spanMs, months } = useMemo(() => {
    const all = [];
    (plan?.blocks || []).forEach(b => { if (b.start_date) all.push(d(b.start_date)); if (b.end_date) all.push(d(b.end_date)); });
    if (!all.length) return { minMs: 0, spanMs: 1, months: [] };
    const min = new Date(Math.min(...all.map(x => x.getTime())));
    const max = new Date(Math.max(...all.map(x => x.getTime())));
    const minM = new Date(min.getFullYear(), min.getMonth(), 1);
    const maxM = new Date(max.getFullYear(), max.getMonth() + 1, 1);
    const mo = [];
    let cur = new Date(minM);
    while (cur < maxM && mo.length < 120) { mo.push(new Date(cur)); cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1); }
    return { minMs: minM.getTime(), spanMs: Math.max(maxM.getTime() - minM.getTime(), DAY), months: mo };
  }, [plan]);

  const pos = (start, end) => {
    const s = d(start), e = d(end);
    if (!s || !e) return null;
    const left = Math.max(0, ((s.getTime() - minMs) / spanMs) * 100);
    const width = Math.max(0.6, ((e.getTime() - s.getTime()) / spanMs) * 100);
    return { left: `${left}%`, width: `${width}%` };
  };

  const todayPos = useMemo(() => {
    const t = Date.now();
    if (t < minMs || t > minMs + spanMs) return null;
    return `${((t - minMs) / spanMs) * 100}%`;
  }, [minMs, spanMs]);

  const freezeBaseline = (replace = false) => {
    if (!planId) return;
    setFreezing(true);
    apiPost(`/project-planning/plans/${planId}/freeze-baseline${replace ? '?confirm=replace' : ''}`)
      .then(() => { toast.success(replace ? 'Baseline re-frozen' : 'Baseline frozen'); loadPlan(planId); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Freeze failed'))
      .finally(() => setFreezing(false));
  };

  const toggleCritical = async () => {
    const next = !showCritical;
    setShowCritical(next);
    if (next && criticalIds.size === 0 && projectId) {
      setCpmLoading(true);
      try {
        const res = await apiGet(`/primavera/cpm/${projectId}`);
        const ids = new Set();
        (res.tasks || []).filter(t => t.is_critical).forEach(t => { ids.add(t.task_id); ids.add(t.code); });
        setCriticalIds(ids);
        if (ids.size === 0) toast.info('No critical activities found (add predecessor links).');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not compute critical path');
        setShowCritical(false);
      } finally { setCpmLoading(false); }
    }
  };
  useEffect(() => { setShowCritical(false); setCriticalIds(new Set()); }, [projectId, planId]);

  const Bar = ({ item, isBlock }) => {
    const row = bvaById[item.id];
    const status = computeStatus(item, row);
    const tone = STATUS_COLOR[status];
    const isCritical = showCritical && !isBlock && (criticalIds.has(item.id) || criticalIds.has(item.code));
    const barColor = isCritical ? 'bg-rose-600 ring-1 ring-rose-700' : tone.bar;
    const p = pos(item.start_date, item.end_date);
    const bp = (plan?.baseline_frozen_at && item.baseline_start) ? pos(item.baseline_start, item.baseline_end) : null;
    return (
      <div className="relative h-7" onClick={() => setSelected({ item, isBlock, status, row })}>
        {bp && <div className="absolute top-0 h-1.5 rounded-sm bg-slate-300/80 border border-slate-400/40" style={bp} title="Baseline" />}
        {p && (
          <div
            className={`absolute ${bp ? 'top-2.5' : 'top-1'} h-4 rounded-sm ${barColor} cursor-pointer hover:brightness-110 transition-all flex items-center px-1.5 ${isBlock ? 'opacity-100' : 'opacity-90'}`}
            style={p}
            data-testid={`gantt-bar-${item.id}`}
            title={`${item.code} · ${item.name}\n${fmt(item.start_date)} → ${fmt(item.end_date)}${isCritical ? '\n★ On critical path' : ''}${row ? `\nVariance: ${row.schedule_variance_days}d (${row.status})` : ''}`}
          >
            {isBlock && <span className="text-[9px] text-white font-bold truncate">{item.code}</span>}
          </div>
        )}
      </div>
    );
  };

  if (!projectId) return null;

  return (
    <div data-testid="project-gantt-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="kpi-overline">Schedule</div>
          <h3 className="font-display font-bold text-base mt-1">Gantt Chart · Baseline vs Current</h3>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {plans.length > 1 && (
            <select value={planId} onChange={e => setPlanId(e.target.value)} data-testid="gantt-plan-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]">
              {plans.map(p => <option key={p.id} value={p.id}>{p.title || p.id.slice(0, 8)} · {p.block_count || 0} blocks</option>)}
            </select>
          )}
          <button onClick={toggleCritical} disabled={cpmLoading || !blocks.length} data-testid="gantt-critical-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm border disabled:opacity-50 ${showCritical ? 'bg-rose-600 text-white border-rose-700' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            <GitBranch size={12} className={cpmLoading ? 'animate-pulse' : ''} /> {cpmLoading ? 'Analysing…' : 'Critical path'}
          </button>
          {plan && (plan.baseline_frozen_at ? (
            <>
              <span className="text-[11px] text-slate-500 flex items-center gap-1"><Lock size={12} /> Baseline frozen {fmt(plan.baseline_frozen_at)}</span>
              <button onClick={() => freezeBaseline(true)} disabled={freezing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="gantt-rebaseline-btn">
                <RefreshCw size={12} className={freezing ? 'animate-spin' : ''} /> Re-baseline
              </button>
            </>
          ) : (
            <button onClick={() => freezeBaseline(false)} disabled={freezing || !blocks.length} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] disabled:opacity-50" data-testid="gantt-freeze-btn">
              <Flag size={12} /> Freeze Baseline
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px] text-slate-600">
        {Object.entries(STATUS_COLOR).filter(([k]) => k !== 'unknown').map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-2.5 rounded-sm ${v.bar}`} /> {v.label}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm bg-slate-300 border border-slate-400/40" /> Baseline</span>
        {showCritical && <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-rose-600" /> Critical path</span>}
      </div>

      {loading && <div className="text-center text-xs text-slate-400 uppercase tracking-widest py-16 font-display">Loading schedule…</div>}

      {!loading && plan && blocks.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-sm text-slate-500" data-testid="gantt-empty">
          This plan has no blocks yet. Generate the schedule in <button onClick={() => navigate('/app/project-planning')} className="text-[#3b4872] underline">Project Planning · AI</button>.
        </div>
      )}

      {!loading && !plan && !plans.length && (
        <div className="text-center py-12 text-slate-400 text-sm" data-testid="gantt-no-plan">No AI plan yet for this project. Generate one in Project Planning · AI to see the Gantt chart.</div>
      )}

      {!loading && plan && blocks.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="gantt-chart">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0">
                <div className="w-[320px] shrink-0 px-3 py-2 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 border-r border-slate-200">WBS Item</div>
                <div className="flex-1 relative h-8">
                  {months.map((mo, i) => (
                    <div key={i} className="absolute top-0 h-8 border-l border-slate-200 text-[9px] text-slate-500 px-1 pt-1 font-mono"
                      style={{ left: `${((new Date(mo.getFullYear(), mo.getMonth(), 1).getTime() - minMs) / spanMs) * 100}%` }}>
                      {formatMonth(mo)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                {todayPos && (
                  <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `calc(320px + ${todayPos} * (100% - 320px) / 100)` }} title="Today" />
                )}
                {blocks.map((b) => {
                  const micros = microByBlock[b.code] || [];
                  const isOpen = expanded[b.code];
                  return (
                    <div key={b.id}>
                      <div className="flex items-center border-b border-slate-100 hover:bg-slate-50">
                        <div className="w-[320px] shrink-0 px-3 py-1.5 border-r border-slate-200 flex items-center gap-1">
                          {micros.length > 0 ? (
                            <button onClick={() => setExpanded(s => ({ ...s, [b.code]: !s[b.code] }))} className="text-slate-400 hover:text-slate-700" data-testid={`gantt-expand-${b.code}`}>
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : <span className="w-3.5" />}
                          <span className="text-[12px] font-display font-bold text-[#0F172A] truncate">{b.code} · {b.name}</span>
                          {micros.length > 0 && <span className="text-[9px] text-slate-400 ml-auto">{micros.length}</span>}
                        </div>
                        <div className="flex-1 px-1"><Bar item={b} isBlock /></div>
                      </div>
                      {isOpen && micros.map((t) => (
                        <div key={t.id} className="flex items-center border-b border-slate-50 hover:bg-slate-50/60">
                          <div className="w-[320px] shrink-0 pl-9 pr-3 py-1 border-r border-slate-200">
                            <span className="text-[11px] text-slate-600 truncate block">{t.code} · {t.name}</span>
                          </div>
                          <div className="flex-1 px-1"><Bar item={t} isBlock={false} /></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="gantt-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-left pr-6">{selected.item.code} · {selected.item.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest ${STATUS_COLOR[selected.status].bar} text-white`}>
                  {STATUS_COLOR[selected.status].label}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Current Start</div><div className="font-medium">{fmt(selected.item.start_date)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Current End</div><div className="font-medium">{fmt(selected.item.end_date)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Baseline Start</div><div className="font-medium">{fmt(selected.item.baseline_start)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Baseline End</div><div className="font-medium">{fmt(selected.item.baseline_end)}</div></div>
                </div>
                {selected.row && (
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Schedule Variance</div>
                    <div className={`text-lg font-display font-bold ${selected.row.schedule_variance_days > 0 ? 'text-red-600' : selected.row.schedule_variance_days < 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {selected.row.schedule_variance_days > 0 ? '+' : ''}{selected.row.schedule_variance_days} days
                    </div>
                    <div className="text-[11px] text-slate-500">vs baseline finish</div>
                  </div>
                )}
                {selected.item.resources && <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Resources</div><div className="text-sm">{selected.item.resources}</div></div>}
                <button onClick={() => navigate(`/app/project-planning/${planId}`)} className="inline-flex items-center gap-1.5 text-[12px] font-display font-bold text-[#3b4872] hover:underline" data-testid="gantt-open-planner">
                  <ExternalLink size={13} /> Edit in Planner
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
